/**
 * The typed form fields a request can ask for, beside uploads, agreements and
 * signatures: what each one's empty answer is, whether it is answered, what
 * is wrong with it, and how it reads in the folder and the summary PDF.
 *
 * Kept free of React so the rules are tested on their own (test/rules.test.js).
 * The agent-side list of types is DocumentBuilder.jsx in epro-internal-tool.
 */
import { formatDateInput, formatUsPhone, normalizeUsDate } from "./soaForm.js";

/** Types the client fills in by typing or picking — one answer each. */
export const INPUT_TYPES = ["text", "paragraph", "email", "phone", "number", "date", "select", "radio", "checkboxes", "checkbox", "toggle", "name", "address"];

/** Types that only show something: nothing to fill in, nothing to count. */
export const DISPLAY_TYPES = ["note", "heading"];

export const NAME_PARTS = [
  { key: "first", label: "First name ｜ 名", autoComplete: "given-name" },
  { key: "last", label: "Last name ｜ 姓", autoComplete: "family-name" },
];

export const ADDRESS_PARTS = [
  { key: "street", label: "Street address ｜ 街道地址", autoComplete: "address-line1", required: true },
  { key: "line2", label: "Apt, suite, unit ｜ 公寓/单元", autoComplete: "address-line2", required: false },
  { key: "city", label: "City ｜ 城市", autoComplete: "address-level2", required: true },
  { key: "state", label: "State ｜ 州", autoComplete: "address-level1", required: true },
  { key: "zip", label: "ZIP code ｜ 邮编", autoComplete: "postal-code", required: true },
];

export function isInputType(type) {
  return INPUT_TYPES.includes(type);
}

export function isDisplayType(type) {
  return DISPLAY_TYPES.includes(type);
}

/** Options as the builder saved them, blanks dropped. */
export function optionsOf(block) {
  return (Array.isArray(block.options) ? block.options : []).map((o) => String(o ?? "").trim()).filter(Boolean);
}

export function emptyAnswer(type) {
  if (type === "checkbox" || type === "toggle") return false;
  if (type === "checkboxes") return [];
  if (type === "name") return { first: "", last: "" };
  if (type === "address") return { street: "", line2: "", city: "", state: "", zip: "" };
  return "";
}

export function isAnswered(block, value) {
  // A toggle is always an answer — off is "No".
  if (block.type === "toggle") return true;
  if (block.type === "checkbox") return value === true;
  if (block.type === "checkboxes") return Array.isArray(value) && value.length > 0;
  if (block.type === "name") return NAME_PARTS.some((p) => (value?.[p.key] || "").trim());
  if (block.type === "address") return ADDRESS_PARTS.some((p) => (value?.[p.key] || "").trim());
  return Boolean(String(value ?? "").trim());
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** MM/DD/YYYY that is a real calendar date, 1900–2100 (a date of birth fits). */
export function isValidDate(value) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || "");
  if (!match) return false;
  const [month, day, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || year < 1900 || year > 2100) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * What is wrong with one field's answer, in both languages, or null.
 * An optional field left empty is fine; one filled in must still be valid.
 */
export function answerError(block, value) {
  const required = block.required !== false;
  if (!isAnswered(block, value)) {
    if (!required) return null;
    return block.type === "checkbox" ? "Please tick this box ｜ 请勾选此项" : "This is required ｜ 此项必填";
  }
  const text = String(value ?? "").trim();
  switch (block.type) {
    case "email":
      return EMAIL.test(text) ? null : "Enter a valid email ｜ 请输入有效的电子邮件";
    case "phone":
      return text.replace(/\D/g, "").length === 10 ? null : "Enter a 10-digit phone number ｜ 请输入10位电话号码";
    case "number":
      return Number.isFinite(Number(text.replace(/,/g, ""))) ? null : "Enter a number ｜ 请输入数字";
    case "date":
      return isValidDate(text) ? null : "Enter a date as MM/DD/YYYY ｜ 请以 月/日/年 格式输入";
    case "name":
      return NAME_PARTS.every((p) => (value?.[p.key] || "").trim()) ? null : "Enter first and last name ｜ 请输入名和姓";
    case "address":
      return ADDRESS_PARTS.every((p) => !p.required || (value?.[p.key] || "").trim()) ? null : "Complete the address ｜ 请填写完整地址";
    default:
      return null;
  }
}

/** The answer as one plain string for the .txt file and the summary PDF. */
export function answerText(block, value) {
  if (block.type === "checkbox" || block.type === "toggle") return value === true ? "Yes" : "No";
  if (block.type === "checkboxes") return (Array.isArray(value) ? value : []).join(", ");
  if (block.type === "name") return [value?.first, value?.last].map((s) => (s || "").trim()).filter(Boolean).join(" ");
  if (block.type === "address") {
    const v = value || {};
    const cityLine = [(v.city || "").trim(), [(v.state || "").trim(), (v.zip || "").trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return [(v.street || "").trim(), (v.line2 || "").trim(), cityLine].filter(Boolean).join("\n");
  }
  return String(value ?? "").trim();
}

/** Formatting applied as the client types: phone and date masks. */
export function maskInput(type, raw) {
  if (type === "phone") return formatUsPhone(raw);
  if (type === "date") return formatDateInput(raw);
  return raw;
}

/**
 * The steps a form is split into, as the client walks them: the distinct
 * `step` numbers in order (a block without one is on the first). One entry
 * means the form is a single page. A step the agent left empty never shows.
 */
export function formSteps(blocks) {
  return [...new Set((blocks || []).map((b) => Number(b.step) || 0))].sort((a, b) => a - b);
}

// ─── Filled in from the link ─────────────────────────────────────────────────
// `?carrier=aetna-dsnp-089` fills the field whose URL name is "carrier": the
// one the agent set in the builder (`urlKey`), else the English half of its
// label ("Carrier ｜ 保险公司" → carrier). Case, spaces, dashes and
// underscores are ignored, so plan-name, plan_name and planName all match.
// The builder shows the same name (urlKeyOf in epro-internal-tool's
// formBlocks.js) — keep the two alike.

/** Letters and digits only, lower case: how a parameter and a field are compared. */
function squash(text) {
  return String(text ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** The name a link uses for this field, as the builder shows it; "" when it has none. */
export function urlKeyOf(block) {
  const source = (block.urlKey || "").trim() || String(block.label || "").split(/[｜|]/)[0];
  return source.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

const TRUE_WORDS = ["yes", "true", "1", "on", "y"];
const FALSE_WORDS = ["no", "false", "0", "off", "n"];

/** One option matching `raw` as written, or loosely ("aetna-dsnp-089" → "Aetna DSNP 089"). */
function matchOption(block, raw) {
  const options = optionsOf(block);
  const text = String(raw ?? "").trim();
  return options.find((o) => o === text) || options.find((o) => o.toLowerCase() === text.toLowerCase()) || options.find((o) => squash(o) && squash(o) === squash(text)) || null;
}

/** A link's text as this field's answer, or undefined when it cannot be one. */
function linkAnswer(block, raws, parts) {
  const raw = raws[raws.length - 1] ?? "";
  switch (block.type) {
    case "checkbox":
    case "toggle":
      if (TRUE_WORDS.includes(raw.trim().toLowerCase())) return true;
      if (FALSE_WORDS.includes(raw.trim().toLowerCase())) return false;
      return undefined;
    case "select":
    case "radio":
      return matchOption(block, raw) ?? undefined;
    case "checkboxes": {
      // ?extras=dental,vision or ?extras=dental&extras=vision
      const picked = raws.flatMap((r) => r.split(",")).map((r) => matchOption(block, r)).filter(Boolean);
      return picked.length ? [...new Set(picked)] : undefined;
    }
    case "phone": {
      // +1 (215) 825-8435 → the 10 digits.
      const digits = raw.replace(/\D/g, "");
      return formatUsPhone(digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits);
    }
    case "date": {
      const iso = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/.exec(raw);
      return iso ? `${iso[2].padStart(2, "0")}/${iso[3].padStart(2, "0")}/${iso[1]}` : normalizeUsDate(raw);
    }
    case "name":
    case "address": {
      const value = emptyAnswer(block.type);
      if (block.type === "name" && raw.trim()) {
        // ?name=Wei Chen: the last word is the last name.
        const words = raw.trim().split(/\s+/);
        value.last = words.length > 1 ? words.pop() : "";
        value.first = words.join(" ");
      }
      for (const [key, partRaw] of Object.entries(parts)) {
        value[key] = key === "state" ? partRaw.trim().toUpperCase().slice(0, 2) : partRaw.trim();
      }
      return isAnswered(block, value) ? value : undefined;
    }
    default:
      return raw;
  }
}

/**
 * What the link fills in, by block id: `{ value, valid }`. `valid` is whether
 * the answer passes the field's own check — only then may a field set to
 * "locked" or "hidden" be kept from the client; a bad value stays editable.
 * Name and address parts come as `?name.first=` / `?address.zip=`.
 */
export function prefillFromLink(blocks, search) {
  const params = [...new URLSearchParams(search || "").entries()];
  const out = {};
  for (const block of blocks || []) {
    if (!isInputType(block.type)) continue;
    const key = squash(urlKeyOf(block));
    if (!key) continue;
    const raws = [];
    const parts = {};
    const partKeys = block.type === "name" ? NAME_PARTS : block.type === "address" ? ADDRESS_PARTS : [];
    for (const [name, raw] of params) {
      if (squash(name) === key) raws.push(raw);
      const dot = name.lastIndexOf(".");
      if (dot > 0 && squash(name.slice(0, dot)) === key) {
        const part = partKeys.find((p) => p.key === squash(name.slice(dot + 1)));
        if (part) parts[part.key] = raw;
      }
    }
    if (!raws.length && !Object.keys(parts).length) continue;
    const value = linkAnswer(block, raws, parts);
    if (value === undefined || (typeof value === "string" && !value.trim())) continue;
    out[block.id] = { value, valid: isAnswered(block, value) && !answerError({ ...block, required: true }, value) };
  }
  return out;
}

/** How a filled-in field shows: "edit" (default), "lock" or "hide" — and only "edit" when its value is no good. */
export function prefillMode(block, filled) {
  if (!filled) return "edit";
  const mode = block.prefill === "lock" || block.prefill === "hide" ? block.prefill : "edit";
  return filled.valid ? mode : "edit";
}
